import React, { createContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/core';
import { authApi } from '../../api/namespaces/auth';
import { User } from '../../types';
import { AxiosError } from 'axios';

/**
 * Interface for the AuthContext
 * @interface AuthContextType
 */
interface AuthContextType {
    /** Whether the user is authenticated */
    isAuthenticated: boolean;
    /** Whether the user is an admin */
    isAdmin: boolean;
    /** User data if authenticated, otherwise null */
    user: User | null;
    /** Function to log in a user */
    login: (token: string) => Promise<void>;
    /** Function to register a new user */
    register: (email: string, password: string) => Promise<void>;
    /** Function to log out the user */
    logout: () => Promise<void>;
    /** Function to start OAuth login flow */
    startOAuthLogin: (provider: string) => Promise<void>;
    /** Function to refresh user data */
    refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthProvider component
 * @function AuthProvider
 * @param {React.ReactNode} children - Child components to provide authentication context to
 * @returns JSX element
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    /** State for authentication status */
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    /** State for admin status */
    const [isAdmin, setIsAdmin] = useState(false);
    /** State for user data */
    const [user, setUser] = useState<User | null>(null);
    const navigate = useNavigate();

    // Initialize auth state from stored token
    React.useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            // Decode token to check age
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const issueTime = new Date((payload.exp - (30 * 60)) * 1000); // 30 min is default expiry
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);

                // If token is more than a week old, try to refresh it
                if (issueTime < weekAgo) {
                    authApi.refreshToken(token)
                        .then(response => {
                            const newToken = response.access_token;
                            localStorage.setItem('token', newToken);
                            api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                            return api.get('/auth/me');
                        })
                        .then(response => {
                            const userData = response.data;
                            setIsAuthenticated(true);
                            setIsAdmin(userData.is_admin);
                            setUser(userData);
                            if (userData.scheduled_deletion) {
                                navigate('/delete-account');
                            }
                        })
                        .catch(error => {
                            // Only clear auth state on 401s
                            if (error instanceof AxiosError && error.response?.status === 401) {
                                localStorage.removeItem('token');
                                delete api.defaults.headers.common['Authorization'];
                            }
                            console.error('Error refreshing token:', error);
                        });
                } else {
                    // Token is less than a week old, use it directly
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    api.get('/auth/me')
                        .then(response => {
                            const userData = response.data;
                            setIsAuthenticated(true);
                            setIsAdmin(userData.is_admin);
                            setUser(userData);
                            if (userData.scheduled_deletion) {
                                navigate('/delete-account');
                            }
                        })
                        .catch(error => {
                            // Only clear auth state on 401s
                            if (error instanceof AxiosError && error.response?.status === 401) {
                                localStorage.removeItem('token');
                                delete api.defaults.headers.common['Authorization'];
                            }
                            console.error('Error fetching user data:', error);
                        });
                }
            } catch {
                // If token is invalid, clear it
                localStorage.removeItem('token');
                delete api.defaults.headers.common['Authorization'];
            }
        }
    }, [navigate]);

    /**
     * Logs in a user with a given token
     * @param {string} token - Authentication token
     */
    const login = useCallback(async (token: string) => {
        try {
            // Store token
            localStorage.setItem('token', token);
            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            
            // Get user data
            const response = await api.get('/auth/me');
            const userData = response.data;
            
            setIsAuthenticated(true);
            setIsAdmin(userData.is_admin);
            setUser(userData);

            // If account is scheduled for deletion, redirect to change password
            if (userData.scheduled_deletion && window.location.pathname !== '/delete-account') {
                navigate('/delete-account');
            }
        } catch (error) {
            // Only clear auth state on 401s
            if (error instanceof AxiosError && error.response?.status === 401) {
                setIsAuthenticated(false);
                setIsAdmin(false);
                setUser(null);
            }
            throw error;
        }
    }, [navigate]);

    /**
     * Registers a new user
     * @param {string} email - User's email address
     * @param {string} password - User's password
     */
    const register = useCallback(async (email: string, password: string) => {
        await api.post('/auth/register', { email, password });
    }, []);

    /**
     * Logs out the user
     */
    const logout = useCallback(async () => {
        try {
            await api.post('/auth/logout');
        } finally {
            localStorage.removeItem('token');
            delete api.defaults.headers.common['Authorization'];
            setIsAuthenticated(false);
            setIsAdmin(false);
            setUser(null);
            window.location.reload(); // Force a full page refresh
        }
    }, []);

    /**
     * Starts OAuth login flow for a provider
     * @param {string} provider - OAuth provider (google or facebook)
     */
    const startOAuthLogin = useCallback(async (provider: string) => {
        const response = await authApi.startOAuthLogin(provider);
        // OAuth URLs must use window.location.href since they are external
        window.location.href = response.auth_url;
    }, []);

    // Refresh user data
    const refreshUser = useCallback(async () => {
        try {
            const response = await api.get('/auth/me');
            const userData = response.data;
            setIsAuthenticated(true);
            setIsAdmin(userData.is_admin);
            setUser(null); // Force a re-render by clearing user first
            setUser(userData);

            // Check for scheduled deletion
            if (userData.scheduled_deletion) {
                navigate('/delete-account');
            }
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 401) {
                setIsAuthenticated(false);
                setIsAdmin(false);
                setUser(null);
            }
            console.error('Failed to refresh user data:', error);
        }
    }, [navigate]);

    // Handle auth:redirect events
    React.useEffect(() => {
        const handleRedirect = (event: CustomEvent<{ path: string }>) => {
            const path = event.detail.path;
            if (path) {
                navigate(path);
            }
        };

        window.addEventListener('auth:redirect', handleRedirect as EventListener);
        return () => {
            window.removeEventListener('auth:redirect', handleRedirect as EventListener);
        };
    }, [navigate]);

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            isAdmin,
            user,
            login,
            register,
            logout,
            startOAuthLogin,
            refreshUser
        }}>
            {children}
        </AuthContext.Provider>
    );
};
